import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";

interface ScraperQuota {
  maxJobsPerDay: number;
  maxPagesPerJob: number;
  maxPagesPerMonth: number;
  jobsToday: number;
  pagesThisMonth: number;
  isVelaroAdmin: boolean;
  autoRebuildEnabled: boolean;
  rebuildIntervalDays: number;
  rebuildIntervalLabel: string;
  lastAutoRebuildAt: string | null;
  nextAutoRebuildAt: string | null;
}

interface FileUploadQuota {
  maxPagesPerMonth: number;
  pagesThisMonth: number;
  ocrPagesThisMonth: number;
  maxFilesTotal: number;
  totalFilesIndexed: number;
  ocrEnabled: boolean;
  ocrModelAlias: string;
}

const OCR_CAPABLE_MODELS = [
  { alias: "velaro-gpt-4o",    label: "GPT-4o (Recommended — best for dense scanned documents)" },
  { alias: "velaro-gpt-4",     label: "GPT-4 Turbo (Legacy vision)" },
  { alias: "gemini-2.0-flash", label: "Gemini 2.0 Flash (Fast, cost-efficient)" },
  { alias: "gemini-1.5-pro",   label: "Gemini 1.5 Pro" },
];

function UsageBar({ current, max, label }: { current: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((current / max) * 100)) : 0;
  const color =
    pct >= 90 ? "bg-crimson-red-500" : pct >= 70 ? "bg-sweet-orange-400" : "bg-primary";
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>{label}</span>
        <span>
          {current.toLocaleString()} / {max > 0 ? max.toLocaleString() : "∞"}
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function ScraperLimitsPanel() {
  const api = useApi();
  const [quota, setQuota] = useState<ScraperQuota | null>(null);
  const [fileQuota, setFileQuota] = useState<FileUploadQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingOcr, setEditingOcr] = useState(false);
  const [savingOcr, setSavingOcr] = useState(false);
  const [form, setForm] = useState({ maxJobsPerDay: 0, maxPagesPerJob: 0, maxPagesPerMonth: 0 });
  const [ocrForm, setOcrForm] = useState({
    ocrEnabled: false,
    ocrModelAlias: "velaro-gpt-4o",
    maxUploadedPagesPerMonth: 0,
    maxUploadedFilesTotal: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerMsg, setTriggerMsg] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const [quotaResp, fileResp] = await Promise.all([
        api.admin.get("ScraperJobs/Quota"),
        api.admin.get("ScraperJobs/FileQuota"),
      ]);
      if (quotaResp.ok) {
        const data: ScraperQuota = await quotaResp.json();
        setQuota(data);
        setForm({
          maxJobsPerDay: data.maxJobsPerDay,
          maxPagesPerJob: data.maxPagesPerJob,
          maxPagesPerMonth: data.maxPagesPerMonth,
        });
      } else {
        setError("Failed to load scraper quota.");
      }
      if (fileResp.ok) {
        const fq: FileUploadQuota = await fileResp.json();
        setFileQuota(fq);
        setOcrForm({
          ocrEnabled: fq.ocrEnabled,
          ocrModelAlias: fq.ocrModelAlias,
          maxUploadedPagesPerMonth: fq.maxPagesPerMonth,
          maxUploadedFilesTotal: fq.maxFilesTotal,
        });
      }
    } catch (ex: unknown) {
      console.error("Failed to load scraper quota:", ex);
      setError("Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleTriggerNow = async () => {
    setTriggering(true);
    setTriggerMsg(null);
    try {
      const resp = await api.admin.post("ScraperJobs/TriggerNow", {});
      setTriggerMsg(resp.ok
        ? "Rebuild triggered — check the Knowledge Base tab for progress."
        : "Failed to trigger rebuild.");
    } catch (ex: unknown) {
      console.error("Failed to trigger scraper rebuild:", ex);
      setTriggerMsg("Failed to connect to server.");
    } finally {
      setTriggering(false);
      setTimeout(() => setTriggerMsg(null), 8000);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await api.admin.put("ScraperJobs/Quota", form);
      if (resp.ok) {
        setEditing(false);
        await fetchAll();
      } else {
        setError("Failed to save quota changes.");
      }
    } catch (ex: unknown) {
      console.error("Failed to save scraper quota:", ex);
      setError("Failed to connect to server.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOcr = async () => {
    setSavingOcr(true);
    try {
      const resp = await api.admin.put("ScraperJobs/OcrSettings", ocrForm);
      if (resp.ok) {
        setEditingOcr(false);
        await fetchAll();
      } else {
        setError("Failed to save OCR settings.");
      }
    } catch (ex: unknown) {
      console.error("Failed to save OCR settings:", ex);
      setError("Failed to connect to server.");
    } finally {
      setSavingOcr(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-slate-400 animate-pulse">Loading scraper limits...</div>
    );
  }

  if (error) {
    return <div className="p-6 text-sm text-crimson-red-600">{error}</div>;
  }

  if (!quota) return null;

  return (
    <div className="p-6 space-y-6 max-w-2xl">

      {/* ── Web Scraper Section ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-gray-800">Web Scraper Limits</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Quotas enforced per site. Resets: jobs/pages-today at midnight UTC, pages/month on the 1st.
          </p>
        </div>
        {quota.isVelaroAdmin && !editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleTriggerNow}
              disabled={triggering}
              className="text-xs px-3 py-1.5 rounded border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50"
            >
              {triggering ? "Triggering…" : "Trigger Rebuild Now"}
            </button>
            <button
              onClick={() => setEditing(true)}
              className="text-xs px-3 py-1.5 rounded border border-cornflower-blue-300 text-primary hover:bg-primary-bg"
            >
              Edit Limits
            </button>
          </div>
        )}
        {triggerMsg && (
          <p className="text-xs text-green-700 mt-1">{triggerMsg}</p>
        )}
      </div>

      {!editing ? (
        <div className="space-y-4">
          <UsageBar label="Scraper jobs today" current={quota.jobsToday} max={quota.maxJobsPerDay} />
          <div className="flex justify-between text-xs text-slate-500">
            <span>Max pages per job</span>
            <span className="font-medium text-slate-700">{quota.maxPagesPerJob.toLocaleString()}</span>
          </div>
          <UsageBar label="Web pages crawled this month" current={quota.pagesThisMonth} max={quota.maxPagesPerMonth} />
        </div>
      ) : (
        <div className="space-y-4 border border-slate-200 rounded-lg p-4 bg-slate-50">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Edit Web Scraper Limits</p>
          {[
            { key: "maxJobsPerDay", label: "Max jobs per day" },
            { key: "maxPagesPerJob", label: "Max pages per job" },
            { key: "maxPagesPerMonth", label: "Max pages per month" },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-3">
              <label className="w-48 text-sm text-slate-600 flex-shrink-0">{label}</label>
              <input
                type="number"
                min={1}
                value={form[key as keyof typeof form]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: parseInt(e.target.value) || 1 }))}
                className="w-32 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-hover"
              />
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-active disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setEditing(false)}
              className="px-4 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50">
              Cancel
            </button>
          </div>
          {error && <p className="text-xs text-crimson-red-600">{error}</p>}
        </div>
      )}

      {/* ── File Upload & OCR Section ── */}
      {fileQuota && (
        <div className="border border-slate-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-700">File Upload & OCR</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                PDF, DOCX, and other files ingested from SharePoint, OneDrive, or manual upload.
              </p>
            </div>
            {quota.isVelaroAdmin && !editingOcr && (
              <button onClick={() => setEditingOcr(true)}
                className="text-xs px-3 py-1.5 rounded border border-cornflower-blue-300 text-primary hover:bg-primary-bg">
                Edit
              </button>
            )}
          </div>

          <UsageBar
            label="Uploaded file pages this month"
            current={fileQuota.pagesThisMonth}
            max={fileQuota.maxPagesPerMonth}
          />

          {fileQuota.ocrPagesThisMonth > 0 && (
            <div className="flex justify-between text-xs text-slate-500">
              <span>Pages processed via OCR this month</span>
              <span className="font-medium text-slate-700">{fileQuota.ocrPagesThisMonth.toLocaleString()}</span>
            </div>
          )}

          <div className="flex justify-between text-xs text-slate-500">
            <span>Total files indexed</span>
            <span className="font-medium text-slate-700">
              {fileQuota.totalFilesIndexed.toLocaleString()} / {fileQuota.maxFilesTotal > 0 ? fileQuota.maxFilesTotal.toLocaleString() : "∞"}
            </span>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
              fileQuota.ocrEnabled
                ? "bg-green-100 text-green-700"
                : "bg-slate-100 text-slate-500"
            }`}>
              {fileQuota.ocrEnabled ? "OCR enabled" : "OCR disabled"}
            </span>
            {fileQuota.ocrEnabled && (
              <span className="text-xs text-slate-400">
                Model: {OCR_CAPABLE_MODELS.find(m => m.alias === fileQuota.ocrModelAlias)?.label?.split(" (")[0] ?? fileQuota.ocrModelAlias}
              </span>
            )}
          </div>

          {!fileQuota.ocrEnabled && (
            <p className="text-xs text-slate-400">
              OCR is off. Image-based PDFs (scanned documents) will be indexed with minimal text extracted.
              Enable OCR to extract text from scanned files using a vision AI model.
            </p>
          )}

          {editingOcr && quota.isVelaroAdmin && (
            <div className="space-y-4 border border-slate-200 rounded-lg p-4 bg-slate-50 mt-2">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Edit OCR & File Upload Settings</p>

              <div className="flex items-center gap-3">
                <label className="text-sm text-slate-600 flex-shrink-0 w-48">OCR enabled</label>
                <input
                  type="checkbox"
                  checked={ocrForm.ocrEnabled}
                  onChange={(e) => setOcrForm(f => ({ ...f, ocrEnabled: e.target.checked }))}
                  className="w-4 h-4 accent-primary"
                />
                <span className="text-xs text-slate-400">
                  Sends image-based PDFs to vision LLM for text extraction
                </span>
              </div>

              {ocrForm.ocrEnabled && (
                <div className="flex items-center gap-3">
                  <label className="text-sm text-slate-600 flex-shrink-0 w-48">OCR model</label>
                  <select
                    value={ocrForm.ocrModelAlias}
                    onChange={(e) => setOcrForm(f => ({ ...f, ocrModelAlias: e.target.value }))}
                    className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-hover"
                  >
                    {OCR_CAPABLE_MODELS.map(m => (
                      <option key={m.alias} value={m.alias}>{m.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center gap-3">
                <label className="text-sm text-slate-600 flex-shrink-0 w-48">Max file pages/month</label>
                <input
                  type="number"
                  min={0}
                  value={ocrForm.maxUploadedPagesPerMonth}
                  onChange={(e) => setOcrForm(f => ({ ...f, maxUploadedPagesPerMonth: parseInt(e.target.value) || 0 }))}
                  className="w-32 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-hover"
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm text-slate-600 flex-shrink-0 w-48">Max files total</label>
                <input
                  type="number"
                  min={0}
                  value={ocrForm.maxUploadedFilesTotal}
                  onChange={(e) => setOcrForm(f => ({ ...f, maxUploadedFilesTotal: parseInt(e.target.value) || 0 }))}
                  className="w-32 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-hover"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button onClick={handleSaveOcr} disabled={savingOcr}
                  className="px-4 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-active disabled:opacity-50">
                  {savingOcr ? "Saving…" : "Save"}
                </button>
                <button onClick={() => setEditingOcr(false)}
                  className="px-4 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Auto-Refresh Schedule ── */}
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">Knowledge Base Auto-Refresh</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            quota.autoRebuildEnabled
              ? "bg-green-100 text-green-700"
              : "bg-slate-200 text-slate-500"
          }`}>
            {quota.autoRebuildEnabled ? quota.rebuildIntervalLabel : "Manual only"}
          </span>
        </div>
        <p className="text-xs text-slate-500">
          {quota.autoRebuildEnabled
            ? `Your AI knowledge base is automatically re-crawled and re-indexed ${quota.rebuildIntervalLabel.toLowerCase()} to keep the bot's answers current.`
            : "Upgrade to Pro or Enterprise to enable automatic knowledge base refresh."}
        </p>
        {quota.autoRebuildEnabled && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div className="text-xs">
              <span className="text-slate-400">Last refresh</span>
              <div className="font-medium text-slate-700 mt-0.5">
                {quota.lastAutoRebuildAt
                  ? new Date(quota.lastAutoRebuildAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                  : "Not yet run"}
              </div>
            </div>
            <div className="text-xs">
              <span className="text-slate-400">Next refresh</span>
              <div className="font-medium text-slate-700 mt-0.5">
                {quota.nextAutoRebuildAt
                  ? new Date(quota.nextAutoRebuildAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                  : quota.lastAutoRebuildAt ? "Scheduled" : "After first manual crawl"}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="text-xs text-slate-400 border-t pt-3 space-y-0.5">
        <p>
          <span className="font-medium">Default web scraper limits:</span> 3 jobs/day · 200 pages/job · 1,000 pages/month
        </p>
        <p>
          <span className="font-medium">OCR note:</span> gpt-4o-mini is excluded from OCR — use GPT-4o or Gemini for scanned documents.
        </p>
        <p>Contact Velaro support to increase limits for your plan.</p>
      </div>
    </div>
  );
}
